import { z } from 'zod'

// These values are part of the v1 wire contract, not caller-supplied policy.
// Coordinate any policy change with a new version and the WPCivi producer fixture.
const CategoryPolicyV1 = {
  aliases: {
    creamed_honey_soft_set: 'creamed_honey',
    varietal_honey_basswood: 'varietal_extracted_honey',
    varietal_honey_black_locust: 'varietal_extracted_honey',
    varietal_honey_buckwheat: 'varietal_extracted_honey',
    varietal_honey_gallberry: 'varietal_extracted_honey',
    varietal_honey_sourwood: 'varietal_extracted_honey',
    varietal_honey_tulip_poplar: 'varietal_extracted_honey',
    varietal_honey_tupelo: 'varietal_extracted_honey',
  },
  fullFrameKeys: ['full_frame_comb_honey_deep', 'full_frame_comb_honey_shallow_medium'],
  labelOverrides: {
    creamed_honey: 'Creamed Honey, natural',
    creamed_honey_firm_set: 'Naturally Granulated Honey',
    creamed_honey_flavored: 'Creamed Honey, flavored',
    full_frame_comb_honey_deep: 'Full Frame Comb Honey (deep)',
    full_frame_comb_honey_shallow_medium: 'Full Frame Comb Honey (shallow/medium)',
  },
  legacyLabels: {
    full_frame_comb_honey: 'Full Frame Comb Honey (legacy selection; frame size unspecified)',
    photography_related_to_beekeeping:
      'Photography Related to Beekeeping (legacy selection; class unspecified)',
  },
  photographyOrder: [
    'photography_open',
    'photography_essay',
    'photography_selected_topic',
    'photography_macro',
    'photography_black_white',
    'photography_scenic',
    'photography_portrait',
  ],
  varietalLabels: {
    varietal_honey_basswood: 'Basswood',
    varietal_honey_black_locust: 'Black Locust',
    varietal_honey_buckwheat: 'Buckwheat',
    varietal_honey_gallberry: 'Gallberry',
    varietal_honey_sourwood: 'Sourwood',
    varietal_honey_tulip_poplar: 'Tulip Poplar',
    varietal_honey_tupelo: 'Tupelo',
  },
  version: 'hsu-category-policy.v1',
} as const
const categoryPolicyV1Canonical = canonicalModelCatalogJson(CategoryPolicyV1)

const Key = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]*$/)
const RowId = z
  .string()
  .regex(/^[1-9][0-9]*$/)
  .refine(value => Number.isSafeInteger(Number(value)), 'Source ID must be a safe integer')
const Text = z.string().refine(value => value.trim().length > 0, 'Required source text')
const Integer = z.number().int().refine(Number.isSafeInteger, 'Expected a safe integer')
const LocalDateTime = z
  .string()
  .refine(
    value =>
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) &&
      z.iso.datetime().safeParse(value.replace(' ', 'T') + 'Z').success,
    'Invalid source local datetime'
  )
const Category = z.strictObject({
  key: Key,
  optionId: RowId,
  value: Text,
  label: Text,
  displayLabel: Text,
  grouping: z.string(),
  weight: Integer,
  isActive: z.boolean(),
  kind: z.enum(['selectable_class', 'parameterized_category', 'legacy_alias', 'legacy_category']),
  displayOrder: Integer.nonnegative().nullable(),
})
const Section = z.strictObject({
  key: Key,
  sourceId: RowId,
  title: z.string(),
  weight: Integer,
  isActive: z.boolean(),
})
const Block = z.strictObject({
  key: Key,
  sectionKey: Key,
  sourceId: RowId,
  kind: z.literal('rule_block'),
  title: z.string(),
  body: z.string(),
  blockType: Text,
  categoryKeys: z.array(Key),
  conditionKey: Text,
  weight: Integer,
  isActive: z.boolean(),
  updatedAtLocal: LocalDateTime,
  presentation: z.strictObject({
    imageFileId: Integer.nonnegative(),
    imageAltText: z.string(),
    imageCaption: z.string(),
    imageAlignment: z.string(),
    imageMaxHeight: Integer.nonnegative(),
    headingLevel: Integer.nonnegative(),
    startOnNewPage: z.boolean(),
    numberStyle: z.string(),
    numberStart: Integer.nonnegative(),
  }),
})
const SourceUrl = z.url().refine(value => {
  const url = new URL(value)
  return (
    ['http:', 'https:'].includes(url.protocol) &&
    !url.username &&
    !url.password &&
    !url.search &&
    !url.hash
  )
}, 'Source URL must not include credentials, query, or fragment')

/** Independent of the per-show WpciviExportBundle. No omitted-array defaults. */
export const WpciviModelCatalogBundle = z
  .strictObject({
    schemaVersion: z.literal('wpcivi-model-catalog.v1'),
    exportedAt: z.iso.datetime(),
    source: z.strictObject({
      system: z.literal('wpcivi'),
      instanceId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
      url: SourceUrl,
    }),
    scope: z.strictObject({
      kind: z.literal('global_model_catalog'),
      completeness: z.literal('complete'),
      includesInactive: z.literal(true),
      missingRecords: z.literal('no_deletion'),
      subtypeCoverage: z.literal('legacy_keys_only'),
    }),
    contentDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    compatibilityPolicy: z.strictObject({
      version: z.literal('hsu-category-policy.v1'),
      aliases: z.record(Key, Key),
      varietalLabels: z.record(Key, Text),
      labelOverrides: z.record(Key, Text),
      legacyLabels: z.record(Key, Text),
      photographyOrder: z.array(Key),
      fullFrameKeys: z.array(Key),
    }),
    categories: z.array(Category).min(1),
    aliases: z.array(
      z.strictObject({
        key: Key,
        targetKey: Key,
        configured: z.boolean(),
        semantics: z.literal('legacy_selection'),
      })
    ),
    subtypes: z.array(
      z.strictObject({
        key: Key,
        parentKey: Key,
        label: Text,
        identityBasis: z.literal('legacy_category_option'),
      })
    ),
    publication: z.strictObject({
      isBaseline: z.boolean(),
      createdAtLocal: LocalDateTime,
      sourceId: RowId,
      name: Text,
      version: Text,
      publicationVersionKey: Text,
      sourceRuleSetId: RowId.nullable(),
      effectiveDate: z.iso.date(),
      publishedAtLocal: LocalDateTime,
      updatedAtLocal: LocalDateTime,
      changeSummary: z.string(),
      documentSettings: z.strictObject({
        logo_layout: z.literal('left_right'),
        logo_max_height: z.number().int().min(24).max(240),
      }),
      sections: z.array(Section).min(1),
      blocks: z.array(Block).min(1),
    }),
    diagnostics: z.array(
      z.strictObject({
        code: z.enum([
          'missing_alias_target',
          'missing_subtype_crosswalk',
          'missing_rule_category',
        ]),
        key: Text,
        targetKey: Key.nullable(),
      })
    ),
  })
  .superRefine((bundle, ctx) => {
    const problem = (message: string) => ctx.addIssue({ code: 'custom', message })
    const unique = (values: string[], label: string) => {
      if (new Set(values).size !== values.length) problem(`Duplicate ${label}`)
    }
    unique(
      bundle.categories.map(row => row.key),
      'category key'
    )
    unique(
      bundle.categories.map(row => row.optionId),
      'category source ID'
    )
    unique(
      bundle.categories.map(row => row.value),
      'category value'
    )
    unique(
      bundle.aliases.map(row => row.key),
      'alias key'
    )
    unique(
      bundle.subtypes.map(row => row.key),
      'subtype key'
    )
    unique(
      bundle.publication.sections.map(row => row.key),
      'section key'
    )
    unique(
      bundle.publication.sections.map(row => row.sourceId),
      'section source ID'
    )
    unique(
      bundle.publication.blocks.map(row => `${row.sectionKey}/${row.key}`),
      'block key'
    )
    unique(
      bundle.publication.blocks.map(row => row.sourceId),
      'block source ID'
    )
    const categories = new Map(bundle.categories.map(row => [row.key, row]))
    const sections = new Map(bundle.publication.sections.map(row => [row.key, row]))
    if (bundle.publication.version !== bundle.publication.publicationVersionKey)
      problem('Conflicting publication versions')
    const expectedDiagnostics = new Set<string>()
    const diagnosticKey = (code: string, key: string, targetKey: string | null) =>
      JSON.stringify([code, key, targetKey])
    const policy = bundle.compatibilityPolicy
    if (canonicalModelCatalogJson(policy) !== categoryPolicyV1Canonical)
      problem('Compatibility policy contents do not match hsu-category-policy.v1')
    if (bundle.scope.completeness === 'complete' && !bundle.scope.includesInactive)
      problem('Complete scope must include inactive categories')
    const activeOrders = bundle.categories.filter(row => row.isActive).map(row => row.displayOrder)
    if (
      new Set(activeOrders).size !== activeOrders.length ||
      activeOrders.some(order => order === null || order >= activeOrders.length)
    )
      problem('Active display order must be unique and contiguous')
    for (const category of bundle.categories) {
      const expectedKind = Object.hasOwn(policy.aliases, category.key)
        ? 'legacy_alias'
        : Object.hasOwn(policy.legacyLabels, category.key)
          ? 'legacy_category'
          : category.key === 'varietal_extracted_honey'
            ? 'parameterized_category'
            : 'selectable_class'
      if (category.kind !== expectedKind) problem('Category classification disagrees with policy')
      const expectedLabel = Object.hasOwn(policy.legacyLabels, category.key)
        ? policy.legacyLabels[category.key]
        : Object.hasOwn(policy.labelOverrides, category.key)
          ? policy.labelOverrides[category.key]
          : category.label
      if (category.displayLabel !== expectedLabel)
        problem('Category display label disagrees with policy')
      if (category.isActive !== (category.displayOrder !== null))
        problem('Category active state disagrees with display order')
    }
    // Reproduce the producer's source-weight grouping, then the request catalog's
    // photography override within its arts/crafts group. Full-frame order remains
    // source-defined; fullFrameKeys is an identity list, not an ordering override.
    const displayGroups = new Map<string, typeof bundle.categories>()
    for (const category of [...bundle.categories].sort(
      (a, b) => a.weight - b.weight || Number(a.optionId) - Number(b.optionId)
    )) {
      if (!category.isActive) continue
      const group = category.grouping || 'Show Categories'
      const rows = displayGroups.get(group) ?? []
      rows.push(category)
      displayGroups.set(group, rows)
    }
    let expectedOrder = 0
    for (const [group, rows] of displayGroups) {
      let ordered = rows
      if (group === 'ARTS & CRAFTS RELATED TO BEEKEEPING (Category A)') {
        const firstPhoto = rows.findIndex(row => policy.photographyOrder.includes(row.key))
        if (firstPhoto !== -1) {
          ordered = rows.filter(row => !policy.photographyOrder.includes(row.key))
          const photos = policy.photographyOrder.flatMap(key => rows.filter(row => row.key === key))
          ordered.splice(firstPhoto, 0, ...photos)
        }
      }
      for (const category of ordered) {
        if (category.displayOrder !== expectedOrder++)
          problem('Category display order disagrees with source weights and policy')
      }
    }
    if (bundle.aliases.length !== Object.keys(policy.aliases).length)
      problem('Alias records must cover the compatibility policy')
    for (const alias of bundle.aliases) {
      if (alias.configured !== categories.has(alias.key))
        problem('Alias availability disagrees with category records')
      if (
        !Object.hasOwn(policy.aliases, alias.key) ||
        policy.aliases[alias.key] !== alias.targetKey
      )
        problem('Alias disagrees with compatibility policy')
      if (!categories.has(alias.targetKey))
        expectedDiagnostics.add(diagnosticKey('missing_alias_target', alias.key, alias.targetKey))
    }
    const expectedSubtypes = bundle.aliases.filter(
      alias =>
        alias.configured &&
        categories.has(alias.targetKey) &&
        Object.hasOwn(policy.varietalLabels, alias.key)
    )
    if (expectedSubtypes.length !== bundle.subtypes.length)
      problem('Source-backed varietal subtypes must be preserved')
    for (const subtype of bundle.subtypes) {
      if (
        !expectedSubtypes.some(
          alias => alias.key === subtype.key && alias.targetKey === subtype.parentKey
        ) ||
        policy.varietalLabels[subtype.key] !== subtype.label
      )
        problem('Subtype lacks source-backed parent/alias identity')
    }
    if (categories.has('varietal_extracted_honey'))
      expectedDiagnostics.add(
        diagnosticKey('missing_subtype_crosswalk', 'varietal_extracted_honey', null)
      )
    for (const block of bundle.publication.blocks) {
      if (!sections.has(block.sectionKey)) problem('Block references an absent section')
      unique(block.categoryKeys, 'rule category reference')
      for (const key of block.categoryKeys) {
        if (!categories.has(key))
          expectedDiagnostics.add(
            diagnosticKey('missing_rule_category', `${block.sectionKey}/${block.key}`, key)
          )
      }
    }
    const suppliedDiagnostics = bundle.diagnostics.map(row =>
      diagnosticKey(row.code, row.key, row.targetKey)
    )
    unique(suppliedDiagnostics, 'diagnostic')
    if (
      suppliedDiagnostics.length !== expectedDiagnostics.size ||
      suppliedDiagnostics.some(key => !expectedDiagnostics.has(key))
    )
      problem('Diagnostics must describe exactly the unresolved source references')
    if (
      !bundle.publication.blocks.some(
        block => block.isActive && sections.get(block.sectionKey)?.isActive
      )
    )
      problem('Publication has no active content')
  })

export type TWpciviModelCatalogBundle = z.infer<typeof WpciviModelCatalogBundle>

/** UTF-8 key order, unescaped Unicode/slashes, safe integers, semantic array order. */
export function canonicalModelCatalogJson(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'string')
    return JSON.stringify(value)
  if (typeof value === 'number' && Number.isSafeInteger(value)) return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalModelCatalogJson).join(',')}]`
  if (
    typeof value === 'object' &&
    value !== null &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    const record = value as Record<string, unknown>
    const keys = Object.keys(record).sort((a, b) => {
      const left = Array.from(a, char => char.codePointAt(0)!)
      const right = Array.from(b, char => char.codePointAt(0)!)
      for (let i = 0; i < Math.min(left.length, right.length); i++) {
        if (left[i] !== right[i]) return left[i] - right[i]
      }
      return left.length - right.length
    })
    return `{${keys.map(key => `${JSON.stringify(key)}:${canonicalModelCatalogJson(record[key])}`).join(',')}}`
  }
  throw new Error('Catalog JSON supports only JSON values with safe integer numbers')
}

/** Hash these UTF-8 bytes with SHA-256; schema validation alone does not verify a digest. */
export function modelCatalogDigestInput(bundle: TWpciviModelCatalogBundle): string {
  const { exportedAt: _exportedAt, contentDigest: _contentDigest, ...content } = bundle
  return canonicalModelCatalogJson(content)
}
